import { formatUserClockTime, parseUtcTimestampToMs } from "./timezone";

// Shared presentation helpers for live market update cards (Home and
// CoTrader). Mirrors the GlobalLink pre-trade insights feed.

const SENTIMENT_THRESHOLD = 0.12;
const UPCOMING_RELEASE_PATTERN =
  /\bupcoming\b|\bscheduled\b|\bto be released\b|\beconomic data releases?\b/;

export const clampScore = (value) => Math.max(-1, Math.min(1, value));

// Signed two-decimal score with a true minus sign, e.g. "+0.24" / "−0.40".
export const formatScore = (value) => {
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(2)}`;
};

const formatCountdown = (diffMs) => {
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return minutes > 0 ? `${minutes}m` : "now";
};

// Same thresholds and labels InsightTile uses for its impact badge.
export const getSentiment = (update) => {
  if (!update) return null;
  if (update.type === "count_down") {
    return { tone: "event", label: "Event", score: null };
  }

  if (update.impact_score == null || update.impact_score === "") return null;
  const score = Number(update.impact_score);
  if (!Number.isFinite(score)) return null;

  const clamped = clampScore(score);
  const scoreText = formatScore(clamped);
  if (clamped > SENTIMENT_THRESHOLD) {
    return { tone: "positive", label: "Supportive", score: scoreText };
  }
  if (clamped < -SENTIMENT_THRESHOLD) {
    return { tone: "negative", label: "Negative", score: scoreText };
  }
  return { tone: "neutral", label: "Neutral", score: scoreText };
};

// Presentation for a market update card: its kind (live update, upcoming or
// released data), the icon and accent tone, and the footer status text.
// `timestamp` is when the update arrived; render it with <TimeSince />.
export const getUpdateDisplay = (update, nowMs) => {
  const isScheduled =
    update.type === "count_down" ||
    UPCOMING_RELEASE_PATTERN.test(String(update.title || "").toLowerCase());

  if (isScheduled) {
    const releaseMs = parseUtcTimestampToMs(update.start_date);
    const hasReleaseTime = Number.isFinite(releaseMs);
    const isReleased = hasReleaseTime && releaseMs <= nowMs;

    if (isReleased) {
      return {
        tone: "released",
        kind: "Data released",
        icon: "fa-regular fa-calendar-check",
        timestamp: update.timestamp,
        status: `Released ${formatUserClockTime(releaseMs)}`,
      };
    }

    return {
      tone: "upcoming",
      kind: "Upcoming release",
      icon: "fa-regular fa-clock",
      timestamp: update.timestamp,
      status: hasReleaseTime ? `Due in ${formatCountdown(releaseMs - nowMs)}` : null,
    };
  }

  const sentiment = getSentiment(update);
  const icons = {
    positive: "fa-solid fa-arrow-trend-up",
    negative: "fa-solid fa-arrow-trend-down",
    neutral: "fa-solid fa-minus",
  };

  return {
    tone: sentiment?.tone || "plain",
    kind: "Market update",
    icon: icons[sentiment?.tone] || "fa-solid fa-bolt",
    timestamp: update.timestamp,
    status: sentiment ? `${sentiment.label} impact · ${sentiment.score}` : null,
  };
};

export const getQuestion = (update) =>
  typeof update?.question === "string" && update.question.trim()
    ? update.question.trim()
    : null;
